/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useCallback, useEffect, useState } from "react";

//@Packages
import { useTranslation } from "react-i18next";

//@Components
import { DataCard } from "../../../reusablecomponents/datacard";

//@Utils
import { GatewayStatus, LOCALE_CONST } from "../../../utils/constants";
import CustomizedTable from "../customizedtable";
import { GatewayPortsProps, PortDetails } from "../../../models/remoteConnect";
import { Button, StatusBadge, TextOverflow, Tooltip } from "@forge/common";
import { LinkedDeviceListHeaderProps } from "../../../models/linkedDeviceList";
import { gatewayPortsHeaderData } from "../../../utils/staticdata";
import { ContextualMenuComponent } from "../../../reusablecomponents/contextualmenu";
import { useAddPortGatewayMutation, useApproveOrBlockDeviceMutation, useGetGatewayConnectivityDetailsQuery, useRemovePortDeviceMutation } from "../../../services/remote-connect-api";

import './styles.scss';
import AddDevicePortModal from "../Modal/AddDevicePortModal";
import Toast from "../../../reusablecomponents/toast";
import ConnectConfirmModal from "../Modal/ConnectConfirmModal";
import { gatewayErrorStatusMessages, handleApiError } from "../../../utils/utility";

const GatewayConnectivity = ({ hrefUrl, gatewayType }: { hrefUrl?: string, gatewayType: string }) => {
    const { t } = useTranslation();
    const convertFn = (val: string) => t(val);
    const getDeviceID = () => sessionStorage.getItem("linkedDevicesSystemGuid") || "";
    const { data: gatewayConnectivityDetails,
        isLoading: isGatewayConnectivityLoading,
        isFetching: isGatewayConnectivityFetching,
        refetch: refetchGatewayDetails,
        isError: isActiveConListError,
        error: isGatewayConnectivityError

    } = useGetGatewayConnectivityDetailsQuery({ gatewayId: getDeviceID() });
    const [addPortForGateway, { isLoading, error: isAddPortError }] = useAddPortGatewayMutation();

    // Mutation removing a port to the gateway
    const [removePortDevice, { isLoading: isDeletePortLoading,
        error: isRemovePortError }] = useRemovePortDeviceMutation();

    // Mutation to approve or block a device
    const [approveOrBlock, { isLoading: isApproveOrBlockLoading,
        error: isApproveOrBlockError }] = useApproveOrBlockDeviceMutation();


    //gateway ports header and filter
    const [tableHeadersGatewayPorts, setTableHeadersGatewayPorts] =
        useState<LinkedDeviceListHeaderProps[]>(gatewayPortsHeaderData);
    const [filterHeadersGatewayPorts, setFilterHeadersGatewayPorts] =
        useState<LinkedDeviceListHeaderProps[]>(gatewayPortsHeaderData);
    const [showAddGatewayPortModal, setAddGatewayPortalModal] = useState(false);

    const [searchValue, setSearchValue] = useState<string>("");
    const [filteredPorts, setFilteredPorts] = useState<PortDetails[]>([]);
    const [isPortUpdated, setIsPortUpdated] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState<
        "error" | "success" | "warning" | "progress"
    >("success");
    const [toastContent, setToastContent] = useState("");
    const [deviceId, setDeviceId] = useState<string>("");
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [modalData, setModalData] = useState<any>({
        title: "",
        content: "",
        footerLeftButtonText: "",
        footerRightButtonText: "",
        confirmButtonVariant: "",
        confirm: () => { }
    });
    // Initialize filteredPorts when gatewayConnectivityDetails.ports is loaded
    useEffect(() => {
        if (gatewayConnectivityDetails?.ports) {
            setFilteredPorts(gatewayConnectivityDetails.ports);
        }
    }, [gatewayConnectivityDetails?.ports]);

    //toast message timer to hide the toast after 3 seconds
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [showToast]);



    const getActionMenuItems = useCallback((item: PortDetails) => {
        return [{
            id: `${item["port"]}-remove`,
            label: t(LOCALE_CONST.removePort),
            onClick: () => { openRemovePortHandler(item) }
        }];
    }, []);

    const normalizeSpaces = (str: string) => str.toLowerCase().replace(/\s+/g, ' ').trim();

    // Function to handle search input change
    const searchOnChange = (searchValue: string) => {
        const searchValueLower = normalizeSpaces(searchValue);
        setSearchValue(searchValueLower);

        const filtered = gatewayConnectivityDetails?.ports?.filter((port: PortDetails) =>
            Object.values(port || {}).some((value) =>
                value != null && value.toString().toLowerCase().startsWith(searchValueLower)
            )
        );

        // If no matches, set filteredPorts to an empty array
        setFilteredPorts(filtered?.length > 0 ? filtered : []);
        console.log("Filtered Ports:", filtered);
    };


    // Handler for opening the confirmation modal
    const onModalOpenHandler = (item: any, data: any) => {
        setShowConfirmationModal(true);
        if (item === "Remove device Port") {
            setModalData({
                title: t(LOCALE_CONST.removePort),
                content: t(LOCALE_CONST.removalWishConfirm, { portName: data.name }),
                footerLeftButtonText: t(LOCALE_CONST.cancel),
                footerRightButtonText: t(LOCALE_CONST.confirm),
                confirmButtonVariant: "destructive-primary",
                confirm: () => {
                    removePortApiHandler(data);
                }
            });
        }
        else if (item === "NOT_AUTHORIZED") {
            setModalData({
                title: t(LOCALE_CONST.confirmAuthorisation),
                content: t(LOCALE_CONST.portAuthConfirmation),
                footerLeftButtonText: t(LOCALE_CONST.cancel),
                footerRightButtonText: t(LOCALE_CONST.authorise),
                confirmButtonVariant: "primary",
                confirm: () => {
                    approveOrBlockPortApiHandler(data);
                }
            });
        }
        else if (item === "AUTHORIZED") {
            setModalData({
                title: t(LOCALE_CONST.revokeAuthorisation),
                content: t(LOCALE_CONST.revokeAuthorisationConfirmation),
                footerLeftButtonText: t(LOCALE_CONST.cancel),
                footerRightButtonText: t(LOCALE_CONST.revoke),
                confirmButtonVariant: "destructive-primary",
                confirm: () => {
                    approveOrBlockPortApiHandler(data);
                }
            });
        }

    }

    const addPortModalHandler = () => {
        setAddGatewayPortalModal(true);
    };

    // Function to open the modal for removing a device port
    const openRemovePortHandler = (item: PortDetails) => {
        onModalOpenHandler("Remove device Port", item)
        setDeviceId(item.id);
    }

    const showSuccessToast = (message: any) => {
        setToastType("success");
        setToastContent(message);
        setShowToast(true);
    };

    const showErrorToast = (message: string) => {
        setToastType("error");
        setToastContent(message);
        setShowToast(true);
    };

    const extractErrorMessage = (error: string): string => {
        const match = error?.match(/\[(.*?)\]/);
        return match ? match[1] : error;
    };

    const addPortApiHandler = async ({ localPort, portUsage }: { localPort: string; portUsage: string }) => {
        const payload = {
            port: localPort,
            port_usage: portUsage,
            //portUsage: portUsage,
        }
        setAddGatewayPortalModal(false);
        setIsPortUpdated(true);

        try {
            const response = await addPortForGateway({ gatewayId: getDeviceID(), payload }).unwrap();
            if (response) {
                showSuccessToast(t(LOCALE_CONST.success_ap_200, { gatewayId: getDeviceID() }));
                refetchGatewayDetails();
                setFilteredPorts(gatewayConnectivityDetails?.ports || []);
                setIsPortUpdated(false);
                setSearchValue("");
            }
        } catch (error) {
            console.error("Error adding port:", error?.status);
            const errorMessage = handleApiError(
                error,
                convertFn,
                t(LOCALE_CONST.unknownError),
                {
                    400: t(LOCALE_CONST.badRequest_ap_400),
                    401: t(LOCALE_CONST.unauthorized_ap_401),
                    403: t(LOCALE_CONST.forbidden_ap_403),
                    404: t(LOCALE_CONST.notFound_ap_404),
                    409: t(LOCALE_CONST.conflict_ap_409),
                    500: t(LOCALE_CONST.internalServerError_ap_500),
                }
            );
            showErrorToast(errorMessage);
            setIsPortUpdated(false);
            setSearchValue("");

        }

    };

    //device port remove api handler
    const removePortApiHandler = async (item: PortDetails) => {
        setShowConfirmationModal(false);
        setIsPortUpdated(true);
        try {
            const response = await removePortDevice({ deviceId: item.id, gatewayId: getDeviceID() }).unwrap();
            if (response) {
                showSuccessToast(t(LOCALE_CONST.success_rp_200, { gatewayId: item.id }));
                refetchGatewayDetails();
                //setFilteredData(getLinkedListDetails?.data || []);
                setIsPortUpdated(false);
                setSearchValue("");
            }
        } catch (error) {
            const errorMessage = handleApiError(
                error,
                convertFn,
                t(LOCALE_CONST.unknownError),
                {
                    400: t(LOCALE_CONST.badRequest_rp_400),
                    401: t(LOCALE_CONST.unauthorized_rp_401),
                    403: t(LOCALE_CONST.gatewayPortError),
                    404: t(LOCALE_CONST.notFound_rp_404),
                    500: t(LOCALE_CONST.internalServerError_rp_500),
                }
            );
            showErrorToast(errorMessage);
            setIsPortUpdated(false);
            setSearchValue("");
        }
    };

    //approve or block port api handler
    const approveOrBlockPortApiHandler = async (item: PortDetails) => {
        setShowConfirmationModal(false);
        setIsPortUpdated(true);
                const payload = {
            trust_device: item.auth_status === "AUTHORIZED" ? false : true,
            port: item.port
        };
        try {
            const response = await approveOrBlock({ deviceId: gatewayConnectivityDetails.linked_device_id, gatewayId: getDeviceID(), payload }).unwrap();
            if (response) {
                if (item.auth_status === "AUTHORIZED") {
                    showSuccessToast(t(LOCALE_CONST.success_block_200));
                } else {
                    showSuccessToast(t(LOCALE_CONST.success_approve_200));
                }
                refetchGatewayDetails();
                //setFilteredData(getLinkedListDetails?.data || []);
                setIsPortUpdated(false);
                setSearchValue("");
            }
        } catch (error) {
            const errorMessage = handleApiError(
                error,
                convertFn,
                t(LOCALE_CONST.unknownError),
                {
                    400: t(LOCALE_CONST.badRequest_app_block_400),
                    401: t(LOCALE_CONST.unauthorized_app_block_401),
                    403: t(LOCALE_CONST.forbidden_app_block_403),
                    404: t(LOCALE_CONST.notFound_app_block_404),
                    409: t(LOCALE_CONST.conflict_app_block_409),
                    500: t(LOCALE_CONST.internalServerError_app_block_500),
                }
            );
            showErrorToast(errorMessage);
            setIsPortUpdated(false);
            setSearchValue("");
        }
    }

    const formatContentGatewayPortsData = (content: GatewayPortsProps) => {
        const getColorClsName = (authorisation: string = "") => {
            if (authorisation === "AUTHORIZED") {
                return "green";
            } else {
                return "orange";
            }
        };

        const formatCellContent = (value: any) => value || "-";

        const displayStatus = (authorisation: string) => {
            if (authorisation === "AUTHORIZED") {
                return "Authorised";
            } else if (authorisation === "NOT_AUTHORIZED") {
                return "Not Authorised";
            } else {
                return "-";
            }
        };

        const statusClsName = (status: string) => {
            let clsName = "";

            if (status === GatewayStatus.Error) {
                clsName = "flex items-center";
            }

            return clsName;
        };

        const actionsAuthStatus = (status: string) => {
            if (status === 'AUTHORIZED') {
                return "Revoke";
            }
            else {
                return t(LOCALE_CONST.authorise);
            }
        }

        return content?.ports?.map((item: PortDetails, index: number) => {
            return {
                id: (index + 1).toString(),
                cell: filterHeadersGatewayPorts?.map((header) => {
                    let cellContent = <span>{formatCellContent(item[header.id])}</span>;

                    if (header.id === "auth_status") {
                        cellContent = (
                            <>
                                {item.auth_status ? (
                                <span className={statusClsName(item.auth_status)}>
                                    <StatusBadge className={`mr-1 text-[0.5rem]`}
                                    color={getColorClsName(item.auth_status)}
                                    />
                                    {displayStatus(item.auth_status)}
                                </span>
                                ) : (
                                <span>-</span>
                                )}
                            </>
                        );
                    }
                    else if (header.id === "port_usage") {
                        cellContent = (
                            <TextOverflow
                                resizeDebounceDelay={300}
                                tooltipPosition="top"
                                className="port-usage-text"
                            >
                                {item.port_usage || "-"}
                            </TextOverflow>

                        );
                    }
                    else if (header.id === "port") {
                        cellContent = (
                            <span className="port-text">{item.port || ""}</span>
                        );
                    }
                    else if (header.id === "actions") {
                        cellContent = (
                            <div className="ports-action-button-container">
                                <div className="ports-action-button">
                                    <Button
                                        aria-label="Settings"
                                        data-testid="edit-device"
                                        className="action-remote-connect"
                                        variant="quiet-secondary"
                                        onClick={() => onModalOpenHandler((item.auth_status || ""), item)}
                                    >
                                        {actionsAuthStatus(item.auth_status || "")}
                                    </Button>
                                </div>
                                <div className="ports-action-Contextual-button">
                                    <ContextualMenuComponent
                                        menuItems={getActionMenuItems(item) || []}
                                    />
                                </div>
                            </div>
                        );
                    }

                    return { content: cellContent };
                })
            };
        });
    };
    const gatewayConnectivityData = [
        {
            label: "Local Gateway IP",
            value: gatewayConnectivityDetails?.host,
        },
        {
            label: "MAC ID",
            value: gatewayConnectivityDetails?.mac_id,
        },
        {
            label: "Gateway Web URL",
            hrefUrl: gatewayConnectivityDetails?.remote_url?.core_remote_web_app || undefined,
            value: gatewayConnectivityDetails?.remote_url?.core_remote_web_app ? undefined : "-",
            hideRow: gatewayType == "EBI-Platform",
        },
        {
            label: "Remote Agent Health",
            uiValue: gatewayConnectivityDetails?.remote_agent_health,
        },
        {
            //mapping Companion App - companion_app
            label: "Companion App",
            hrefUrl: gatewayConnectivityDetails?.remote_url?.companion_app,
            hideRow: gatewayType !== "EBI-Platform",
        },
        {
            //mapping ZIWC - ziwc
            label: "EBI Web Client",
            hrefUrl: gatewayConnectivityDetails?.remote_url?.ziwc,
            hideRow: gatewayType !== "EBI-Platform",
        }
    ];

    const getHealthColorClsName = (status: string = "") => {
        if (status === "Connected") {
            return "green";
        } else {
            return "gray";
        }
    };

    const getHealthStatusValue = (status: string = "") => {
        if (status === "Connected") {
            return "Connected";
        } else {
            return "Disconnected";
        }
    };

    // Filter out rows where hideRow is true
    const filteredGatewayConnectivityData = gatewayConnectivityData.filter(row => !row.hideRow);

    return (
        <div className="gateway-connectivity-container">

            <DataCard title={t(LOCALE_CONST.gatewayConnectivity)}
                deviceData={filteredGatewayConnectivityData}
                gatewayPortUI={
                    <div className="gateway-ports-container">
                        <CustomizedTable
                            title={t(LOCALE_CONST.gatewayPorts)}
                            searchOnChange={searchOnChange}
                            searchValue={searchValue}
                            onSearchClear={() => {
                                setSearchValue("");
                                setFilteredPorts(gatewayConnectivityDetails?.ports || []); // Reset to original list
                            }}
                            onExportAction={() => { }}
                            tableData={filteredPorts}
                            tableHeaderData={filterHeadersGatewayPorts}
                            tableContentData={formatContentGatewayPortsData({
                                ports: filteredPorts.length > 0 ? filteredPorts : gatewayConnectivityDetails?.ports,
                                host: "",
                                mac_id: "",
                                remote_url: {
                                    core_remote_web_app: "",
                                    kubernetes: "",
                                    companion_app: "",
                                    ziwc: "",
                                    lxd: "",
                                    metrics_app: ""
                                },
                                linked_device_id: "",
                                remote_agent_health: ""
                            })}
                            addPortalClick={addPortModalHandler}
                            isError={isActiveConListError}
                            errorText={
                                isGatewayConnectivityError && 'status' in isGatewayConnectivityError
                                    ? gatewayErrorStatusMessages(
                                        convertFn,
                                        t(LOCALE_CONST.unknownError),
                                        isGatewayConnectivityError.status,
                                        {
                                            401: t(LOCALE_CONST.unauthorized_gc_401),
                                            403: t(LOCALE_CONST.forbidden_gc_403),
                                            404: t(LOCALE_CONST.notFound_gc_404),
                                            500: t(LOCALE_CONST.internalServerError_gc_500),

                                        }
                                    ) // Display only the status code
                                    : "An error occurred"
                            }
                        />
                    </div>
                }
                getColorClsName={getHealthColorClsName}
                getStatusValue={getHealthStatusValue}
                enableLoader={isGatewayConnectivityLoading || isPortUpdated || isGatewayConnectivityFetching}


            />
            <AddDevicePortModal
                isOpen={showAddGatewayPortModal}
                onClose={() => setAddGatewayPortalModal(false)}
                onConfirm={addPortApiHandler}
                title={t(LOCALE_CONST.addDevicePort)}
                content={t(LOCALE_CONST.definePortForConnecion)}
                footerLeftButtonText={t(LOCALE_CONST.cancel)}
                footerRightButtonText={t(LOCALE_CONST.add)}
                confirmButtonVariant="primary"
            />

            {/* Confirmation Modal */}
            <ConnectConfirmModal
                isOpen={showConfirmationModal}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={modalData.confirm}
                title={modalData.title}
                content={modalData.content}
                footerLeftButtonText={modalData.footerLeftButtonText}
                footerRightButtonText={modalData.footerRightButtonText}
                confirmButtonVariant={modalData.confirmButtonVariant}
            />

            {showToast && (
                <div className="toast-container">
                    <Toast
                        id="delete-toast"
                        showClose={true}
                        showDismiss={false}
                        title={toastContent}
                        type={toastType}
                        lifetime={3000}
                        subtitle={""}
                        content={""}
                    />
                </div>
            )}
        </div>
    );
};

export default GatewayConnectivity;